<template>
  <div class="flex flex-col min-h-full items-center w-full bg-gray-950">
    <div class="w-full min-h-[56px] bg-gray-900 border-b border-gray-800 relative gap-3 flex items-center justify-end pr-6">
      <img class="h-6 absolute left-6 top-0 translate-y-[16px]" :src="$logo" :alt="$t('play.community_servers.logo_alt')">
      <router-link :to="{ name: 'Login', query: { join: slug } }" class="px-3 py-2.5 bg-gray-800 rounded-md text-text-primary text-xs font-medium hover:bg-gray-700">
        {{ $t('play.lan_party.log_in') }}
      </router-link>
      <router-link :to="{ name: 'Register', query: { join: slug } }" class="px-3 py-2.5 bg-primary-600 rounded-md text-text-primary text-xs font-medium hover:bg-primary-500">
        {{ $t('play.lan_party.sign_up') }}
      </router-link>
    </div>
    <AlertPopup />

    <div class="w-full max-w-7xl mx-auto p-4 md:p-9 flex flex-col gap-9 text-text-primary">
      <template v-if="notFound || (creator && !creator.lan_party)">
        <div class="max-w-xl flex flex-col gap-4">
          <h1 class="text-[28px] font-semibold leading-tight">{{ $t('play.lan_party.expired') }}</h1>
          <p v-if="creator" class="text-text-secondary">{{ $t('play.lan_party.expired_hint', { name: creator.name }) }}</p>
          <router-link :to="{ name: 'Register' }" class="self-start px-5 py-3 bg-primary-600 rounded-lg text-sm font-semibold hover:bg-primary-500">
            {{ $t('play.lan_party.expired_cta') }}
          </router-link>
        </div>
      </template>

      <template v-else-if="!creator && loadError">
        <p class="text-danger-400">{{ $t('play.lan_party.network_error') }}</p>
        <button type="button" @click="load" class="self-start px-3 py-2.5 bg-gray-800 rounded-md text-xs font-medium hover:bg-gray-700">
          {{ $t('play.lan_party.try_again') }}
        </button>
      </template>

      <div v-else-if="creator" class="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_380px] gap-6 items-start">
        <LanHero :creator="creator" class="min-w-0" />

        <aside class="border-t border-gray-800 pt-6 lg:border-t-0 lg:pt-6 lg:bg-gray-900 lg:border lg:rounded-lg lg:p-6 flex flex-col gap-5 self-start lg:row-span-2 lg:sticky lg:top-4">
          <h1 class="text-text-primary font-semibold text-[22px] leading-tight">{{ $t('play.lan_party.join_title') }}</h1>
          <p class="text-sm text-text-secondary">{{ $t('play.lan_party.invite_body', { name: creator.name, mode: creator.lan_party.play_mode?.friendly_name || $t('play.lan_party.matches') }) }}</p>
          <LanChips :lan-party="creator.lan_party" />
          <div class="grid grid-cols-2 rounded-lg border border-gray-800 divide-x divide-gray-800">
            <div class="p-3"><p class="text-xl font-semibold tabular-nums text-text-primary">{{ creator.lan_party.member_count }}</p><p class="text-xs text-text-secondary">{{ $t('play.lan_party.stat_in_the_lan') }}</p></div>
            <div class="p-3"><p class="text-xl font-semibold tabular-nums text-text-primary">{{ creator.lan_party.members.length }}</p><p class="text-xs text-text-secondary">{{ $t('play.lan_party.stat_ready_to_play') }}</p></div>
          </div>
          <router-link
            v-if="signedIn"
            :to="{ name: 'LanPartyPage', params: { slug }, query: { join: '1' } }"
            class="w-full py-4 rounded-lg bg-primary-600 text-white text-base font-semibold text-center hover:bg-primary-500"
          >{{ $t('play.lan_party.join') }}</router-link>
          <div v-else class="border-t border-gray-800 pt-5 flex flex-col gap-3">
            <p class="font-semibold text-sm flex items-center gap-2">
              <img src="../../assets/logos/refrag-icon.svg" alt="" class="w-5 h-5" />
              {{ $t('play.lan_party.needs_account_title') }}
            </p>
            <p class="text-xs text-text-secondary">{{ $t('play.lan_party.needs_account_body', { name: creator.name }) }}</p>
            <router-link :to="{ name: 'Register', query: { join: slug } }" class="w-full py-3 rounded-lg bg-primary-600 text-white text-sm font-semibold text-center hover:bg-primary-500">
              {{ $t('play.lan_party.create_account') }}
            </router-link>
            <router-link :to="{ name: 'Login', query: { join: slug } }" class="w-full py-3 rounded-lg bg-gray-800 text-text-primary text-sm font-semibold text-center hover:bg-gray-700">
              {{ $t('play.lan_party.have_account') }}
            </router-link>
          </div>
        </aside>

        <LanLeaderboard :slug="slug" :creator-name="creator.name" class="min-w-0 lg:col-start-1" />
      </div>
    </div>
  </div>
</template>

<script setup>
import axios from 'axios';
import { ref, computed, onBeforeUnmount } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useHead } from '@unhead/vue';
import { useI18n } from 'vue-i18n';
// eslint-disable-next-line import-x/extensions
import { useAuth } from '@websanova/vue-auth/src/v3.js';
import AlertPopup from '../../components/AlertPopup.vue';
import LanHero from '../../components/LanParty/LanHero.vue';
import LanChips from '../../components/LanParty/LanChips.vue';
import LanLeaderboard from '../../components/LanParty/LanLeaderboard.vue';

const POLL_MS = 15_000;

const { t } = useI18n();
const auth = useAuth();
const router = useRouter();
const route = useRoute();
const { slug } = route.params;
const creator = ref(null);
const notFound = ref(false);
const loadError = ref(false);
let pollTimer;
let unmounted = false;

const signedIn = computed(() => !!auth.user()?.id);

useHead({
  title: computed(() => creator.value && t('play.lan_party.page_title', { name: creator.value.name })),
});

function load() {
  // Signed in, the night lives inside the app with the sidebar.
  if (signedIn.value) {
    router.replace({ name: 'LanPartyPage', params: { slug }, query: { join: '1' } });
    return;
  }
  axios.get(`/public/creators/${slug}`).then(({ data }) => {
    creator.value = data;
    loadError.value = false;
  }).catch((error) => {
    if (error.response?.status === 404) notFound.value = true;
    else loadError.value = true;
  }).finally(() => {
    clearTimeout(pollTimer);
    if (!notFound.value && !unmounted) pollTimer = setTimeout(load, POLL_MS);
  });
}

onBeforeUnmount(() => {
  unmounted = true;
  clearTimeout(pollTimer);
});

load();
</script>
